import { fetchSasUrl, SasUrlResponse } from '../../../services/api/storage.api';
import { ChatResponseFile } from '../../Chat/types';

interface CacheEntry {
  blobUrl: string;
  lastUsed: number;
}

// Bounds how many generated images keep their decoded bytes resident at
// once. Each entry holds a full image (hundreds of KB to a few MB), not
// just a URL string, so this is a real memory trade-off, not just hygiene.
// Kept low deliberately — see clearGeneratedImageCache below for the other
// half of the memory story (everything is also dropped on chat switch/
// logout, not just capped).
const MAX_ENTRIES = 10;

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<string>>();
// Aborted and replaced by clearGeneratedImageCache, cancelling every download
// that belongs to the context being cleared.
let downloadController = new AbortController();

const evictLeastRecentlyUsed = () => {
  let oldestKey: string | null = null;
  let oldestTime = Infinity;
  cache.forEach((entry, key) => {
    if (entry.lastUsed < oldestTime) {
      oldestTime = entry.lastUsed;
      oldestKey = key;
    }
  });
  if (oldestKey) {
    URL.revokeObjectURL(cache.get(oldestKey)!.blobUrl);
    cache.delete(oldestKey);
  }
};

// Resolves a generated image to a local blob: URL, fetching it at most once
// per path for the life of the tab. Keyed by `path`, not `file_id` — chat
// history rows come back with file_id: null, while path is always present
// and is already globally unique (it embeds the user, session, and file
// UUID). Deliberately does NOT hand out the raw Azure SAS URL for repeat
// display (thumbnail, then the "view bigger" modal, then download) —
// requesting the same SAS URL a second time can trigger the browser's
// conditional-GET revalidation (If-None-Match), which Azure Blob Storage
// rejects with a 304/412 "ConditionNotMet" error instead of the image.
// Fetching the bytes once and caching a blob: URL sidesteps that entirely,
// and as a side effect keeps an already-viewed image available even after
// its SAS token would otherwise have expired.
export const resolveGeneratedImage = async (file: ChatResponseFile, userId: string): Promise<string> => {
  const cached = cache.get(file.path);
  if (cached) {
    cached.lastUsed = Date.now();
    return cached.blobUrl;
  }

  const existing = inFlight.get(file.path);
  if (existing) return existing;

  const { signal } = downloadController;
  const promise = (async () => {
    const sasRes = await fetchSasUrl(file.path, userId);
    if (!sasRes.ok) throw new Error('Failed to resolve image URL');
    const { file_url }: SasUrlResponse = await sasRes.json();

    try {
      const imageRes = await fetch(file_url, { cache: 'no-store', signal });
      if (!imageRes.ok) throw new Error('Failed to fetch image');
      const blob = await imageRes.blob();
      const blobUrl = URL.createObjectURL(blob);

      if (cache.size >= MAX_ENTRIES) evictLeastRecentlyUsed();
      cache.set(file.path, { blobUrl, lastUsed: Date.now() });
      return blobUrl;
    } catch (err) {
      // Reading the bytes ourselves failed — most commonly the storage
      // account has no CORS rule allowing this origin, so the browser
      // blocks fetch() from reading the response even though the raw
      // request can show 200 in devtools' Network tab. Fall back to the
      // SAS URL directly: <img src> doesn't need CORS to display an image,
      // only fetch()+blob() does. This bypasses our cache for this image
      // (nothing to revoke, nothing that survives SAS expiry), so repeat
      // access re-resolves a fresh SAS URL each time — CORS should still be
      // fixed on the storage account (allow this origin, GET, for both the
      // blob and dfs endpoints) for full reliability. A download cancelled by
      // clearGeneratedImageCache also lands here; that's expected, not a failure.
      if (!signal.aborted) {
        console.warn('[generatedImageCache] Falling back to direct SAS URL (byte fetch failed):', err);
      }
      return file_url;
    }
  })();

  inFlight.set(file.path, promise);
  try {
    return await promise;
  } finally {
    // Only remove our own entry: after a clear, a newer request for the same
    // path may have registered its own in-flight download here.
    if (inFlight.get(file.path) === promise) inFlight.delete(file.path);
  }
};

// Revokes every cached blob: URL and empties the cache. Call this whenever
// the user is leaving the context these images belonged to — switching
// chats, starting a new chat, or logging out — so decoded image bytes don't
// sit in memory for a conversation that's no longer on screen. In-flight
// downloads are cancelled; anything still waiting on one gets the direct SAS
// URL instead.
export const clearGeneratedImageCache = (): void => {
  downloadController.abort();
  downloadController = new AbortController();
  cache.forEach((entry) => URL.revokeObjectURL(entry.blobUrl));
  cache.clear();
  inFlight.clear();
};
